import { createSelector } from '@reduxjs/toolkit'
import { Evaluation } from 'lib/evaluation'
import { Project } from 'lib/project/types.d'
import { RootState } from 'store/root-reducer'
import { selectCurrentEnvironments } from 'store/slices/environments/selectors'
import { selectCurrentTreeItemRef } from 'store/slices/ui/selectors'
import { selectProjectObjects, selectProjectRootRef } from './selectors'

type CtxEvaluationAbstract<T> = (
  requestRef: Project.GenericRef<Project.Request>,
  ctx: Evaluation.Ctx,
) => T

/**
 * A "selector factory" that creates a selector with a given evaluation
 * function. This way, the entire dynamic value evaluation process can
 * happen inside a single selector.
 * Note: for performance reasons, selector factories should be memoized
 * in the component that is calling it.
 * @param fn The evaluation function
 * @returns A selector for that operation
 */
const selectCtxEvaluationResultFactory = <T>(
  fn: CtxEvaluationAbstract<T>,
): ((state: RootState) => T | null) =>
  createSelector(
    selectProjectObjects,
    selectProjectRootRef,
    selectCurrentTreeItemRef,
    selectCurrentEnvironments,
    (objects, root, treeItemRef, currentEnvironments): T | null => {
      if (!treeItemRef) {
        return null
      }
      const ctx: Evaluation.Ctx = {
        project: {
          objects,
          root,
        },
        currentEnvironments,
        layers: [
          {
            layerType: 'RequestLayer',
            requestRef: treeItemRef,
          },
        ],
      }
      return fn(treeItemRef, ctx)
    },
  )

export default selectCtxEvaluationResultFactory
